import { useEffect, useState, useCallback } from 'react';
import { api } from '../services/api';
import type { AdminTwin, PaginationInfo } from '../types';
import { Layout } from '../components/layout/Layout';
import { TwinsTable } from '../components/twins/TwinsTable';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useToast } from '../components/ui/Toast';
import { Search, X, ChevronLeft, ChevronRight, Bot, Calendar } from 'lucide-react';
import { Input } from '../components/ui/Input';

export function Twins() {
  const { toast } = useToast();
  const [twins, setTwins] = useState<AdminTwin[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [twinType, setTwinType] = useState('');
  const [status, setStatus] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const fetchTwins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getTwins({
        search: searchTerm || undefined,
        twinType: twinType || undefined,
        status: status || undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
        page: currentPage,
        limit: 20,
        sortBy: sortDir === 'desc' ? `-${sortBy}` : sortBy,
      });
      setTwins(response.twins);
      setPagination(response.pagination);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to load twins.', 'error');
    } finally {
      setLoading(false);
    }
  }, [createdFrom, createdTo, currentPage, searchTerm, sortBy, sortDir, status, toast, twinType]);

  useEffect(() => {
    fetchTwins();
  }, [fetchTwins]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setTwinType('');
    setStatus('');
    setCreatedFrom('');
    setCreatedTo('');
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const hasFilters = searchTerm || twinType || status || createdFrom || createdTo;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#f8fafc]">Twins</h2>
            <p className="text-[#94a3b8] mt-1">
              View and manage all AI twins across the platform
            </p>
          </div>
          {pagination && (
            <div className="text-sm text-[#64748b] bg-[#1c1c2a] px-3 py-1.5 rounded-lg border border-[#252535]">
              {pagination.total} twins total
            </div>
          )}
        </div>

        {/* Search and Filters */}
        <Card>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                <input
                  type="text"
                  placeholder="Search by twin name, owner name, or email…"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-9 pr-4 py-2 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] placeholder-[#64748b] focus:outline-none focus:border-[#3b82f6] transition-colors"
                />
              </div>

              {/* Type filter */}
              <select
                value={twinType}
                onChange={(e) => {
                  setTwinType(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6] transition-colors"
              >
                <option value="">All Types</option>
                <option value="company">Company</option>
                <option value="job_seeker">Job Seeker</option>
              </select>

              {/* Status filter */}
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 bg-[#1c1c2a] border border-[#252535] rounded-xl text-sm text-[#f1f5f9] focus:outline-none focus:border-[#3b82f6] transition-colors"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="locked">Locked</option>
                <option value="inactive">Inactive</option>
              </select>

              {/* Clear */}
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters} icon={<X className="w-4 h-4" />}>
                  Clear
                </Button>
              )}
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-[#94a3b8] uppercase tracking-wider shrink-0">
                <Calendar className="w-4 h-4" />
                <span>Created</span>
              </div>
              <Input
                type="date"
                label="From"
                value={createdFrom}
                onChange={(e) => {
                  setCreatedFrom(e.target.value);
                  setCurrentPage(1);
                }}
                className="min-w-[160px] [color-scheme:dark]"
              />
              <Input
                type="date"
                label="To"
                value={createdTo}
                min={createdFrom || undefined}
                onChange={(e) => {
                  setCreatedTo(e.target.value);
                  setCurrentPage(1);
                }}
                className="min-w-[160px] [color-scheme:dark]"
              />
            </div>
          </div>
        </Card>

        {/* Twins Table */}
        <Card padding="none">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
            </div>
          ) : twins.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 bg-[#1c1c2a] rounded-2xl flex items-center justify-center mb-3">
                <Bot className="w-5 h-5 text-[#64748b]" />
              </div>
              <p className="text-sm font-medium text-[#94a3b8]">No twins found</p>
              <p className="text-xs text-[#64748b] mt-1">
                {hasFilters ? 'Try adjusting your filters' : 'Twins will appear here once users create them'}
              </p>
            </div>
          ) : (
            <>
              <TwinsTable
                twins={twins}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                onTwinsChanged={fetchTwins}
              />

              {/* Pagination */}
              {pagination && pagination.total > 0 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-[#2a2a3a]">
                  <p className="text-sm text-[#94a3b8]">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} twins
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={pagination.page <= 1}
                      icon={<ChevronLeft className="w-4 h-4" />}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-[#94a3b8] px-2">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                      disabled={pagination.page >= pagination.totalPages}
                      icon={<ChevronRight className="w-4 h-4" />}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
}
